import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { me, fireWebhook, verifyApp, storeSsoSession } from '../api/apiService';
import Login from './Login';

function SSO() {
  const location = useLocation();
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = React.useState('');
  const [showLogin, setShowLogin] = React.useState(false);

  useEffect(() => {
    const queryParameters = new URLSearchParams(location.search);
    const redirectUrl = queryParameters.get("redirect");
    const webhookUrl = queryParameters.get("webhook");
    const appId = queryParameters.get("appId");
    const requestId = queryParameters.get('requestId');
    const codeChallenge = queryParameters.get('codeChallenge');

    const processLogin = async () => {
      if (!appId) {
        setErrorMsg("ACCESS_DENIED: CONSUMER APPLICATION 'appId' IDENTIFIER MISSING OR INVALID.");
        return;
      }

      try {
        const appDetails = await verifyApp(appId);
        const registeredWebhook = appDetails?.data?.data?.webhookUrl;
        const registeredRedirect = appDetails?.data?.data?.redirectUrl;
        const appName = appDetails?.data?.data?.appName;

        const finalWebhook = registeredWebhook || webhookUrl;
        const finalRedirect = redirectUrl || registeredRedirect;

        if (finalWebhook) {
          localStorage.setItem("webhook", finalWebhook);
        } else {
          localStorage.removeItem("webhook");
        }
        
        if (finalRedirect) {
          localStorage.setItem("redirect", finalRedirect);
        } else {
          localStorage.removeItem("redirect");
        }
        
        if (appName) {
          localStorage.setItem("appName", appName);
        }
        
        localStorage.setItem("appId", appId);

        if (requestId && codeChallenge) {
          localStorage.setItem('sso_requestId', requestId);
          localStorage.setItem('sso_codeChallenge', codeChallenge);
        } else {
          localStorage.removeItem('sso_requestId');
          localStorage.removeItem('sso_codeChallenge');
        }

      } catch (e) {
        setErrorMsg("UPLINK_FAILED: APPLICATION NODE COULD NOT BE VERIFIED IN THE REGISTRY.");
        return;
      }

    const localToken = localStorage.getItem("x-device-access-token");
    const deviceId = localStorage.getItem("x-device-service-id");

      if (localToken) {
        me()
          .then(async () => {
            const storedWebhook = localStorage.getItem("webhook");
            const storedRedirect = localStorage.getItem("redirect");
            const appName = localStorage.getItem("appName");
            
            if (requestId && codeChallenge) {
              try {
                await storeSsoSession({ requestId: requestId, accessToken: localToken, deviceId, codeChallenge: codeChallenge });
              } catch (e) {
                console.error('Failed to store SSO session:', e);
              }
            }

            localStorage.removeItem("redirect");
            localStorage.removeItem("webhook");
            localStorage.removeItem("appId");
            localStorage.removeItem("appName");
            
            if (storedWebhook) {
              await fireWebhook(storedWebhook, localToken, deviceId);
            }
            
            if (storedRedirect) {
              window.location.replace(`${storedRedirect}?accessToken=${localToken}&xDeviceId=${deviceId}`);
            } else {
              let userId = 'user';
              try {
                const payload = JSON.parse(atob(localToken.split('.')[1]));
                userId = payload.userId || 'user';
              } catch (e) {}
              navigate(`/${userId}/profile`, { replace: true, state: { ssoAuthSuccess: true, appName: appName } });
            }
          })
          .catch(() => {
            localStorage.removeItem("x-device-access-token");
            setShowLogin(true);
          });
      } else {
        setShowLogin(true);
      }
    };

    processLogin();
  }, [location, navigate]);

  if (showLogin) {
    return <Login />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden">
      {/* Background Image Layer */}
      <div className="absolute inset-0 cyber-auth-bg" />
      {/* Dynamic Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-[#030305]/80 to-[#030305]/90" />
      
      <div className="relative z-10 flex flex-col items-center w-full max-w-md p-6 text-center">
        {errorMsg ? (
          <div className="cyber-card p-8 border-red-500 shadow-[0_0_20px_rgba(255,0,0,0.3)] w-full">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-red-500 bg-red-900/30 mb-6" style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}>
              <svg className="w-8 h-8 text-red-500 drop-shadow-[0_0_5px_rgba(255,0,0,0.8)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-black text-red-500 tracking-widest uppercase mb-4 drop-shadow-[0_0_8px_rgba(255,0,0,0.4)]">SYSTEM_ERROR</h2>
            <p className="cyber-label text-[10px] text-red-400/80 leading-relaxed">{errorMsg}</p>
          </div>
        ) : (
          <div className="cyber-card p-8 border-cyan-500 shadow-[0_0_20px_rgba(0,255,255,0.2)] w-full">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-cyan-500 bg-cyan-900/30 mb-6" style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}>
              <svg className="w-8 h-8 text-cyan-400 animate-spin drop-shadow-[0_0_5px_rgba(0,255,255,0.8)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <h2 className="text-xl font-black text-cyan-400 tracking-widest uppercase mb-3 drop-shadow-[0_0_8px_rgba(0,255,255,0.4)]">ESTABLISHING_UPLINK</h2>
            <p className="cyber-label text-[10px]">VERIFYING CREDENTIALS... PLEASE STAND BY.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SSO;
